import { useRef, useEffect, useCallback, useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'

/**
 * Reads CSS variables from the document and builds an ECharts-compatible
 * colour palette. Runs at render time so it automatically picks up the
 * current theme (light / dark).
 */
function getCSSVar(name: string): string {
    return getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim()
}

function hslVar(name: string): string {
    const raw = getCSSVar(name)
    return raw ? `hsl(${raw})` : ''
}

function buildThemeColors() {
    return {
        background: hslVar('--background'),
        foreground: hslVar('--foreground'),
        card: hslVar('--card'),
        cardForeground: hslVar('--card-foreground'),
        primary: hslVar('--primary'),
        primaryForeground: hslVar('--primary-foreground'),
        muted: hslVar('--muted'),
        mutedForeground: hslVar('--muted-foreground'),
        border: hslVar('--border'),
        success: hslVar('--success') || 'hsl(142 72% 35%)',
        warning: hslVar('--warning') || 'hsl(38 92% 50%)',
        destructive: hslVar('--destructive') || 'hsl(0 84% 60%)',
        chart1: hslVar('--chart-1'),
        chart2: hslVar('--chart-2'),
        chart3: hslVar('--chart-3'),
        chart4: hslVar('--chart-4'),
        chart5: hslVar('--chart-5'),
    }
}

interface EChartsWrapperProps {
    option: EChartsOption
    style?: React.CSSProperties
    className?: string
    loading?: boolean
    onEvents?: Record<string, (params: any) => void>
    notMerge?: boolean
}

export function EChartsWrapper({
    option,
    style,
    className,
    loading = false,
    onEvents,
    notMerge = false,
}: EChartsWrapperProps) {
    const chartRef = useRef<ReactECharts>(null)
    const themeKey = useRef(0)

    // Observe dark mode toggle and force re-render
    useEffect(() => {
        const observer = new MutationObserver(() => {
            themeKey.current += 1
            chartRef.current?.getEchartsInstance()?.resize()
        })
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        })
        return () => observer.disconnect()
    }, [])

    // Responsive resize
    useEffect(() => {
        const handleResize = () => chartRef.current?.getEchartsInstance()?.resize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Merge theme defaults into the user-provided option
    const themedOption = useMemo(() => {
        const colors = buildThemeColors()
        return {
            backgroundColor: 'transparent',
            textStyle: {
                color: colors.foreground,
                fontFamily: 'inherit',
            },
            ...option,
        }
    }, [option])

    const loadingOption = useMemo(
        () => ({
            text: '',
            color: hslVar('--primary') || '#3b82f6',
            maskColor: 'transparent',
            zlevel: 0,
        }),
        []
    )

    return (
        <ReactECharts
            ref={chartRef}
            option={themedOption}
            notMerge={notMerge}
            style={{ height: '100%', width: '100%', ...style }}
            className={className}
            showLoading={loading}
            loadingOption={loadingOption}
            onEvents={onEvents}
        />
    )
}

export { buildThemeColors, hslVar, getCSSVar }
